import { useEffect, useState } from 'react';
import {
  useParams,
  generatePath,
  useMatch,
  useNavigate,
} from 'react-router-dom';
import isEqual from 'lodash.isequal';
import {
  ButtonPseudo,
  Popup,
  Account,
  ButtonSize,
  SidePanel,
  SidePanelType,
  DateInputRange,
  Select,
  PermissionEnum,
  ButtonPrimary,
  IconDownload,
  ButtonSecondary,
  SelectItemProps,
  ResponseError,
  Checkbox,
  Autocomplete,
  ToggleInput,
} from '@ui/src';
import { useAppSelector, useAppDispatch } from '@store';
import Card from '../../../components/partial/card/card';
import AccountItem from '../../../components/individual/accounts/account-item';
// import AccountsActivityLogs from './activity-log';
import AccountsTransactionStatement from '../../customers/accounts-statement/accounts-statement';
import TopUpPanel from '../../customers/top-up-panel/top-up-panel';
import {
  useBlockIndividualCreditMutation,
  useBlockIndividualDebitMutation,
  useUnblockIndividualCreditMutation,
  useUnblockIndividualDebitMutation,
  useCreateIndividualAccountMutation,
  useCloseIndividualAccountMutation,
  useOpenIndividualAccountMutation,
  useUpdateIndividualVisibilityMutation,
} from '../store/services/individual';
import {
  useLazyGetAccountPrimariesQuery,
  useLazyGetStatementItemsPdfQuery,
  useUpdateCustomersOverdraftBalanceValueMutation,
} from '../../customers/store/services/customers';
import { Balance, CustomerStatus, CustomerType } from '../../customers/types';
import AccountCurrencies from '../../customers/account-currencies/account-currencies';
import getPeriodDateString from '../../customers/helpers/get-period-string';
import { setNotificationVisible } from '../../../../../../store/slice';

import ROUTES from '../../../routes';

import cn from '../information.module.css';

const IndividualCustomerAccounts = (): React.JSX.Element => {
  const [popupVisible, setPopupVisible] = useState(false);
  const [closeOpenError, setCloseOpenError] = useState('');
  const [blockUblockTopupError, setBlockUblockTopupError] = useState('');
  const [blockUblockWithdrawalsError, setBlockUblockWithdrawalsError] =
    useState('');

  const {
    individualCustomers: {
      info: {
        general: { baseInformation, baseInformationIsLoading, balances },
        accounts: { closedBalances },
      },
    },
    customersDictionaries: { cardsEnabled, overdraftEnabled },
  } = useAppSelector((state) => state);

  const { activePermissions } = useAppSelector((state) => state.root);
  const [topUpPanelIsOpen, setTopUpPanelIsOpen] = useState(false);
  const [selectedTopUpId, setSelectedTopUpId] = useState('');
  const [userAccount, setUserAccount] = useState('');
  const [showCurrencies, setShowCurrencies] = useState(false);
  const [selectedAccountCurrenciesId, setSelectedAccountCurrenciesId] =
    useState('');
  const [statementPanelVisible, setStatementPanelVisible] = useState(false);
  const [selectedDateRange, setSelectedDateRange] = useState<Date[]>([]);
  const [selectedFormat, setSelectedFormat] = useState<string>('csv');
  const [selectedAccount, setSelectedAccount] = useState('');
  const [selectedCurrency, setSelectedCurrency] = useState('');
  const [currenciesList, setCurrenciesList] = useState<SelectItemProps[]>([]);
  const [statementError, setStatementError] = useState('');
  const [selectedDateType, setSelectedDateType] = useState('today');
  const [primariesAccounts, setPrimariesAccounts] = useState<SelectItemProps[]>(
    [],
  );
  const [selectedPrimaryAccount, setSelectedPrimaryAccount] = useState('');
  const [visibleToClient, setVisibleToClient] = useState(true);
  const [accountBalances, setBalances] = useState<Balance[] | undefined>(
    undefined,
  );
  const [defaultBalances, setDefaultBalances] = useState<Balance[] | undefined>(
    undefined,
  );

  const { customerId } = useParams();

  const dispatch = useAppDispatch();

  const [getAccountPrimaries] = useLazyGetAccountPrimariesQuery();

  const [
    blockDebit,
    { isLoading: blockDebitInProgress, isSuccess: blockDebitIsSuccess },
  ] = useBlockIndividualDebitMutation();
  const [
    unblockDebit,
    { isLoading: unblockDebitInProgress, isSuccess: unblockDebitIsSuccess },
  ] = useUnblockIndividualDebitMutation();
  const [
    blockCredit,
    { isLoading: blockCreditInProgress, isSuccess: blockCreditIsSuccess },
  ] = useBlockIndividualCreditMutation();
  const [
    unblockCredit,
    { isLoading: unblockCreditInProgress, isSuccess: unblockCreditIsSuccess },
  ] = useUnblockIndividualCreditMutation();
  const [close, { isLoading: closeInProgress, isSuccess: closeIsSuccess }] =
    useCloseIndividualAccountMutation();
  const [open, { isLoading: openInProgress, isSuccess: openIsSuccess }] =
    useOpenIndividualAccountMutation();
  const [
    createIndividualAccount,
    { isLoading: createAccountInProgress, isSuccess: createAccountSuccess },
  ] = useCreateIndividualAccountMutation();
  const [getCustomersStatementPdf, { isLoading, isFetching, isSuccess }] =
    useLazyGetStatementItemsPdfQuery();

  const [updateVisibility, { isLoading: updateVisiblityInProgress }] =
    useUpdateIndividualVisibilityMutation();

  const [
    updateOverdraftBalanceValue,
    { isLoading: updateOverdraftBalanceIsLoading },
  ] = useUpdateCustomersOverdraftBalanceValueMutation();

  const onUpdateOverdraftBalanceValue = async (accountId: string) => {
    if (!isEqual(defaultBalances, accountBalances)) {
      const updatedBalances: Balance[] = [];

      accountBalances?.forEach((item) => {
        const defaultBalance = defaultBalances?.find(
          (balance) => balance.id === item.id,
        );

        if (defaultBalance && !isEqual(defaultBalance, item)) {
          updatedBalances.push(item);
        }
      });

      if (updatedBalances.length) {
        const responses = await Promise.all(
          updatedBalances?.map((item) => {
            const response = updateOverdraftBalanceValue({
              id: customerId as string,
              accountId,
              balanceId: item.id,
              overdraftEnabled: item.overdraftEnabled as boolean,
              overdraftValue: item.overdraftValue as number,
            });

            return response;
          }),
        );

        if (responses) {
          const nextDefaultBalances = defaultBalances?.map((item) => {
            let result = { ...item };

            const currentUpdatedBalance = updatedBalances?.find(
              (balance) => balance.id === item.id,
            );

            if (currentUpdatedBalance) {
              result = currentUpdatedBalance;
            }

            return result;
          });

          setDefaultBalances(nextDefaultBalances);
        }
      }
    }
  };

  const onUpdateVisibility = async ({
    id,
    visibleToClientValue,
  }: {
    id: string;
    visibleToClientValue: boolean;
  }) => {
    await updateVisibility({
      id,
      body: {
        visibleToClient: visibleToClientValue,
        id,
      },
    });
  };

  const onClose = async ({
    id,
    closed,
    comment,
  }: {
    id: string;
    closed: boolean;
    comment: string;
  }) => {
    setCloseOpenError('');

    if (closed) {
      const response = (await close({
        id: customerId as string,
        accountId: id,
        body: {
          comment,
        },
      })) as { error?: { data: { message: string[] } } };

      if (response.error?.data?.message) {
        if (Array.isArray(response.error?.data?.message)) {
          setCloseOpenError(response.error?.data?.message[0]);
        } else {
          setCloseOpenError(response.error?.data?.message);
        }
      }
    } else {
      const response = (await open({
        id: customerId as string,
        accountId: id,
        body: {
          comment,
        },
      })) as { error?: { data: { message: string[] } } };

      if (response.error?.data?.message) {
        if (Array.isArray(response.error?.data?.message)) {
          setCloseOpenError(response.error?.data?.message[0]);
        } else {
          setCloseOpenError(response.error?.data?.message);
        }
      }
    }
  };

  const onBlockTopup = async ({
    id,
    creditBlock,
  }: {
    id: string;
    creditBlock: boolean;
  }) => {
    setBlockUblockTopupError('');

    if (creditBlock) {
      const response = (await blockCredit({
        accountId: id,
        id: customerId as string,
      })) as { error?: { data: { message: string[] } } };

      if (response.error?.data?.message && response.error?.data?.message[0]) {
        setBlockUblockTopupError(response.error?.data?.message[0]);
      }
    } else {
      const response = (await unblockCredit({
        accountId: id,
        id: customerId as string,
      })) as { error?: { data: { message: string[] } } };

      if (response.error?.data?.message && response.error?.data?.message[0]) {
        setBlockUblockTopupError(response.error?.data?.message[0]);
      }
    }
  };

  const onBlockWithdraw = async ({
    id,
    debitBlock,
  }: {
    id: string;
    debitBlock: boolean;
  }) => {
    setBlockUblockWithdrawalsError('');

    if (debitBlock) {
      const response = (await blockDebit({
        accountId: id,
        id: customerId as string,
      })) as { error?: { data: { message: string[] } } };

      if (response.error?.data?.message && response.error?.data?.message[0]) {
        setBlockUblockWithdrawalsError(response.error?.data?.message[0]);
      }
    } else {
      const response = (await unblockDebit({
        accountId: id,
        id: customerId as string,
      })) as { error?: { data: { message: string[] } } };

      if (response.error?.data?.message && response.error?.data?.message[0]) {
        setBlockUblockWithdrawalsError(response.error?.data?.message[0]);
      }
    }
  };

  const onCreateAccount = () => {
    const clientName = `${baseInformation?.firstName} ${baseInformation?.lastName}`;
    const body: {
      description: string;
      primaryId?: string;
      visibleToClient: boolean;
    } = {
      description: clientName,
      visibleToClient,
    };

    if (selectedPrimaryAccount) {
      body.primaryId = selectedPrimaryAccount;
    }

    createIndividualAccount({
      organizationId: customerId as string,
      body,
    });
  };

  const onCloseStatementPanel = () => {
    setSelectedAccount('');
    setSelectedCurrency('');
    setSelectedDateRange([]);
    setCurrenciesList([]);
    setStatementError('');
    setStatementPanelVisible(false);
    setSelectedDateType('today');
    setSelectedFormat('csv');
  };

  const match = useMatch(ROUTES.CUSTOMERS_INDIVIDUAL_ACCOUNTS);

  const navigate = useNavigate();

  const onDownloadStatement = async () => {
    const { dateString, dateFromValue, dateToValue } = getPeriodDateString(
      selectedDateType,
      selectedDateRange,
    );

    const datePeriod = `from ${new Date(
      dateFromValue as Date,
    ).toLocaleDateString()} to ${new Date(
      dateToValue as Date,
    ).toLocaleDateString()}`;

    const baseQuery = `accountId=${selectedAccount}&currencyId=${selectedCurrency}&${dateString}`;

    const currentAccountNumber = baseInformation?.accounts?.find(
      (item) => item.id === selectedAccount,
    )?.fullNumber;

    const response = (await getCustomersStatementPdf({
      query: baseQuery,
      type: selectedFormat,
    })) as {
      error: ResponseError;
      data: any;
    };

    if (response.data) {
      const hiddenElement = document.createElement('a');
      const url = window.URL || window.webkitURL;

      // @ts-ignore
      const blob = url.createObjectURL(response.data);
      hiddenElement.href = blob;
      hiddenElement.target = '_blank';
      hiddenElement.download = `Statements ${
        currenciesList.find((item) => item.value === selectedCurrency)?.name
      } #${currentAccountNumber} ${datePeriod}.${selectedFormat}`;
      hiddenElement.click();
    } else if (response.error) {
      if (response.error.data.message) {
        if (
          Array.isArray(response.error.data.message) &&
          response.error.data.message[0].property === 'coreError'
        ) {
          setStatementError(response.error.data.message[0].value as string);
        } else if (typeof response.error.data.message === 'string') {
          setStatementError(response.error.data.message);
        } else {
          setStatementError('Internal Server Error. Try Again');
        }
      }
    }
  };

  const getAccountPrimariesAction = async () => {
    const response = await getAccountPrimaries();

    if (response) {
      setPrimariesAccounts(
        response?.data?.data.edges?.map((item) => ({
          name: item.node.description,
          value: item.node.id,
        })) as SelectItemProps[],
      );
    }
  };

  useEffect(() => {
    getAccountPrimariesAction();
  }, []);

  useEffect(() => {
    if (customerId && match) {
      navigate(
        generatePath(ROUTES.CUSTOMERS_INDIVIDUAL_ACCOUNTS_TRANSACTIONS, {
          customerId,
        }),
      );
    }
  }, [customerId, match]);

  useEffect(() => {
    if (createAccountSuccess) {
      setPopupVisible(false);
      setSelectedPrimaryAccount('');
      setVisibleToClient(true);

      dispatch(
        setNotificationVisible({
          visible: true,
          text: "Account creation in progress. You'll be notified.",
          type: 'individual-client-account-create',
        }),
      );
    }
  }, [createAccountSuccess]);

  useEffect(() => {
    let title =
      baseInformation?.firstName || baseInformation?.lastName
        ? `${baseInformation?.firstName} ${baseInformation?.lastName || ''}: Accounts`
        : 'Accounts';

    const companyName = localStorage.getItem('companyName');

    if (companyName) {
      title = `${title} | ${companyName}`;
    }

    document.title = title;
  }, [baseInformation?.firstName, baseInformation?.lastName]);

  useEffect(() => {
    if (isSuccess) {
      onCloseStatementPanel();
    }
  }, [isSuccess]);

  let accountsContent;

  if (showCurrencies) {
    accountsContent = (
      <AccountCurrencies
        setSelectedAccountCurrenciesId={setSelectedAccountCurrenciesId}
        setShowCurrencies={setShowCurrencies}
        selectedAccountCurrenciesId={selectedAccountCurrenciesId}
        accounts={baseInformation?.accounts}
        status={baseInformation?.status}
      />
    );
  } else {
    accountsContent = (
      <>
        <div className={cn.accountsItems}>
          <div className={cn.accountsItemsTitle}>
            <div className={cn.accountsItemsTitleText}>Accounts</div>
            {baseInformation?.status !== CustomerStatus.CLOSED && (
              <ButtonPseudo
                text="+ Add new account"
                className={cn.addAccountLink}
                size={ButtonSize.SMALL}
                onClick={() => setPopupVisible(true)}
              />
            )}
          </div>
          {baseInformationIsLoading ? (
            <Card isLoading={baseInformationIsLoading} />
          ) : (
            (baseInformation?.accounts as Account[])?.map((item) => (
              <AccountItem
                key={item.id}
                account={item as any}
                balance={balances?.filter(
                  (balance) => balance.accountId === item.id,
                )}
                onBlockTopup={onBlockTopup}
                onBlockWithdraw={onBlockWithdraw}
                onClose={onClose}
                closeInProgress={closeInProgress || openInProgress}
                blockTopupInProgress={
                  blockCreditInProgress || unblockCreditInProgress
                }
                blockWithdrawInProgress={
                  blockDebitInProgress || unblockDebitInProgress
                }
                setUserAccount={setUserAccount}
                setTopUpPanelIsOpen={setTopUpPanelIsOpen}
                closeIsSuccess={closeIsSuccess}
                openIsSuccess={openIsSuccess}
                blockDebitIsSuccess={blockDebitIsSuccess}
                unblockDebitIsSuccess={unblockDebitIsSuccess}
                blockCreditIsSuccess={blockCreditIsSuccess}
                unblockCreditIsSuccess={unblockCreditIsSuccess}
                closeOpenError={closeOpenError}
                blockUblockTopupError={blockUblockTopupError}
                blockUblockWithdrawalsError={blockUblockWithdrawalsError}
                setShowCurrencies={setShowCurrencies}
                setSelectedAccountCurrenciesId={setSelectedAccountCurrenciesId}
                setStatementPanelVisible={setStatementPanelVisible}
                setSelectedAccount={setSelectedAccount}
                setCurrenciesList={setCurrenciesList}
                onUpdateVisibility={onUpdateVisibility}
                updateVisibilityInProgress={updateVisiblityInProgress}
                overdraftEnabled={overdraftEnabled}
                accountsBalances={accountBalances}
                setBalances={setBalances}
                defaultBalances={defaultBalances}
                setDefaultBalances={setDefaultBalances}
                onUpdateOverdraftBalanceValue={onUpdateOverdraftBalanceValue}
                updateOverdraftBalanceIsLoading={
                  updateOverdraftBalanceIsLoading
                }
                customerId={customerId}
                status={baseInformation?.status}
                closedBalances={closedBalances}
              />
            ))
          )}
        </div>
        <TopUpPanel
          type={CustomerType.INDIVIDUAL}
          topUpPanelIsOpen={topUpPanelIsOpen}
          setTopUpPanelIsOpen={setTopUpPanelIsOpen}
          selectedTopUpId={selectedTopUpId}
          setSelectedTopUpId={setSelectedTopUpId}
          userAccount={userAccount}
          setUserAccount={setUserAccount}
          userAccounts={baseInformation?.accounts?.map((item) => ({
            accountId: item.id,
            account: { fullNumber: item.fullNumber },
            description: item.description,
          }))}
        />
        {/* <AccountsActivityLogs /> */}
        {activePermissions?.includes(
          PermissionEnum.CUSTOMERS_STATEMENTS_LIST,
        ) && (
          <AccountsTransactionStatement
            title="Statement of account"
            transactionsPath={ROUTES.CUSTOMERS_INDIVIDUAL_ACCOUNTS_TRANSACTIONS}
            cardsTransactionsPath={ROUTES.CUSTOMERS_INDIVIDUAL_ACCOUNTS_CARDS}
            accounts={
              baseInformation?.id === customerId
                ? baseInformation?.accounts
                : undefined
            }
            cards={baseInformation?.cards}
            cardsEnabled={cardsEnabled}
            balances={balances}
            accountsPage
          />
        )}
      </>
    );
  }

  return (
    <div>
      <Popup
        title="You want to open a new account?"
        description="If required, you can select primary account"
        onCancelClick={() => {
          setPopupVisible(false);
          setSelectedPrimaryAccount('');
          setVisibleToClient(true);
        }}
        onOkClick={() => onCreateAccount()}
        cancelButtonText="Cancel"
        okButtonText={createAccountInProgress ? 'Loading...' : 'Open account'}
        okButtonDisabled={createAccountInProgress}
        isVisible={popupVisible}
      >
        <>
          <div className={cn.primaryAccountWrapper}>
            <Autocomplete
              name="primary-account"
              title="Primary account"
              placeholder="Select primary account (optional)"
              list={primariesAccounts || []}
              value={selectedPrimaryAccount}
              onChange={(e) =>
                setSelectedPrimaryAccount(e.target.value as string)
              }
              withClearButton
            />
          </div>
          <ToggleInput
            checked={visibleToClient}
            name="visibleToClient"
            onChange={(e) => setVisibleToClient(e.target.checked)}
            title="Account is visible to the client"
          />
        </>
      </Popup>
      <SidePanel
        title="Download account statement"
        isOpen={statementPanelVisible}
        onClose={onCloseStatementPanel}
        type={SidePanelType.AML}
        errorSidepanel={statementError}
        sidepanelExpandedKey="customers-accounts-statements"
        footer={
          <div className={cn.statementPanelControls}>
            <ButtonPrimary
              text={isLoading || isFetching ? 'Loading...' : 'Download'}
              icon={<IconDownload />}
              disabled={
                !selectedCurrency ||
                !selectedDateType ||
                !selectedFormat ||
                isLoading ||
                isFetching ||
                (selectedDateType === 'custom' && !selectedDateRange.length)
              }
              onClick={onDownloadStatement}
              bold
            />
            <ButtonSecondary
              className="button-white"
              text="Close"
              onClick={onCloseStatementPanel}
              bold
            />
          </div>
        }
        closeOnClickOutside
      >
        <>
          <div className={cn.currencyWrapper}>
            <Select
              name={`selectedCurrecny-${selectedAccount}`}
              title="Currency"
              placeholder="Select currency"
              value={selectedCurrency}
              list={currenciesList || []}
              onChange={(e) => setSelectedCurrency(e.target.value as string)}
              fullWidth
            />
          </div>
          <div className={cn.rangeTypeWrapper}>
            <div className={cn.rangeTypeTitle}>Select period</div>
            <div className={cn.radioWrapper}>
              <Checkbox
                name={`selectedDateType-${selectedAccount}`}
                type="radio"
                value="today"
                title="Today"
                checked={selectedDateType === 'today'}
                onChange={() => setSelectedDateType('today')}
              />
            </div>
            <div className={cn.radioWrapper}>
              <Checkbox
                name={`selectedDateType-${selectedAccount}`}
                type="radio"
                value="week"
                title="Week"
                checked={selectedDateType === 'week'}
                onChange={() => setSelectedDateType('week')}
              />
            </div>
            <div className={cn.radioWrapper}>
              <Checkbox
                name={`selectedDateType-${selectedAccount}`}
                type="radio"
                value="one-month"
                title="1 Month"
                checked={selectedDateType === 'one-month'}
                onChange={() => setSelectedDateType('one-month')}
              />
            </div>
            <div className={cn.radioWrapper}>
              <Checkbox
                name={`selectedDateType-${selectedAccount}`}
                type="radio"
                value="two-months"
                title="2 Months"
                checked={selectedDateType === 'two-months'}
                onChange={() => setSelectedDateType('two-months')}
              />
            </div>
            <div className={cn.radioWrapper}>
              <Checkbox
                name={`selectedDateType-${selectedAccount}`}
                type="radio"
                value="three-months"
                title="3 Months"
                checked={selectedDateType === 'three-months'}
                onChange={() => setSelectedDateType('three-months')}
              />
            </div>
            <div className={cn.radioWrapper}>
              <Checkbox
                name={`selectedDateType-${selectedAccount}`}
                type="radio"
                value="six-months"
                title="6 Months"
                checked={selectedDateType === 'six-months'}
                onChange={() => setSelectedDateType('six-months')}
              />
            </div>
            <div className={cn.radioWrapper}>
              <Checkbox
                name={`selectedDateType-${selectedAccount}`}
                type="radio"
                value="year"
                title="1 Year"
                checked={selectedDateType === 'year'}
                onChange={() => setSelectedDateType('year')}
              />
            </div>
            <div className={cn.radioWrapper}>
              <Checkbox
                name={`selectedDateType-${selectedAccount}`}
                type="radio"
                value="custom"
                title="Custom period"
                checked={selectedDateType === 'custom'}
                onChange={() => setSelectedDateType('custom')}
              />
            </div>
            {selectedDateType === 'custom' && (
              <div
                className={`${cn.dateInputRange} ${
                  !selectedDateRange.length ? cn.empty : ''
                }`}
              >
                <div className={cn.dateInputTitle}>
                  Account statement period
                </div>
                <DateInputRange
                  values={selectedDateRange.map((item) => item.toISOString())}
                  onChangeRange={(date) => setSelectedDateRange(date)}
                  placeholder="Select date"
                  withoutTwoPickers
                  withoutCustomSelector
                />
              </div>
            )}
          </div>
          <div className={cn.rangeTypeWrapper}>
            <div className={cn.rangeTypeTitle}>Format</div>
            <div className={cn.radioWrapper}>
              <Checkbox
                name={`selectedFormat-${selectedAccount}`}
                type="radio"
                value="csv"
                title="CSV"
                checked={selectedFormat === 'csv'}
                onChange={() => setSelectedFormat('csv')}
              />
            </div>
            <div className={cn.radioWrapper}>
              <Checkbox
                name={`selectedFormat-${selectedAccount}`}
                type="radio"
                value="pdf"
                title="PDF"
                checked={selectedFormat === 'pdf'}
                onChange={() => setSelectedFormat('pdf')}
              />
            </div>
          </div>
        </>
      </SidePanel>
      <div className={cn.customerContent}>{accountsContent}</div>
    </div>
  );
};

export default IndividualCustomerAccounts;
